import { useState, useEffect, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  Newspaper,
  Plus,
  Upload,
  Trash2,
  Edit,
  Eye,
  EyeOff,
  Calendar,
  User,
  AlertTriangle,
  Anchor,
  Cloud,
  Train,
  Shield,
  Ship,
  Info,
  Loader2,
  FileText,
  Search,
  RefreshCw,
  MessageSquare,
  ThumbsUp,
  Mail,
  ExternalLink,
} from "lucide-react";


// Updated category config with new subscription categories
const CATEGORY_CONFIG = {
  // Primary subscription categories
  operational: { icon: AlertTriangle, color: "bg-blue-600", textColor: "text-blue-600", label: "Operational News", description: "Port delays, terminal updates, customs changes" },
  carrier: { icon: Ship, color: "bg-indigo-500", textColor: "text-indigo-500", label: "Carrier News", description: "Shipping line updates, schedule changes, rate announcements" },
  railway: { icon: Train, color: "bg-purple-500", textColor: "text-purple-500", label: "Railway News", description: "Metrans, rail transport updates, disruptions" },
  industry: { icon: Newspaper, color: "bg-emerald-500", textColor: "text-emerald-500", label: "Industry News", description: "Market trends, regulations, trade updates" },
  company: { icon: Info, color: "bg-sky-500", textColor: "text-sky-500", label: "Company News", description: "Europacific announcements, service updates" },
  // Legacy categories (still supported)
  port_update: { icon: Anchor, color: "bg-blue-500", textColor: "text-blue-500", label: "Port Update" },
  weather: { icon: Cloud, color: "bg-orange-500", textColor: "text-orange-500", label: "Weather Alert" },
  rail: { icon: Train, color: "bg-purple-500", textColor: "text-purple-500", label: "Rail Disruption" },
  customs: { icon: Shield, color: "bg-green-500", textColor: "text-green-500", label: "Customs Notice" },
  shipping_line: { icon: Ship, color: "bg-indigo-500", textColor: "text-indigo-500", label: "Shipping Line" },
  general: { icon: Info, color: "bg-gray-500", textColor: "text-gray-500", label: "General" },
};

// Primary subscription categories for the category selector
const SUBSCRIPTION_CATEGORIES = [
  { value: "operational", label: "Operational News" },
  { value: "carrier", label: "Carrier News" },
  { value: "railway", label: "Railway News" },
  { value: "industry", label: "Industry News" },
  { value: "company", label: "Company News" },
  { value: "general", label: "General" },
];

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low", color: "text-gray-500" },
  { value: "normal", label: "Normal", color: "text-blue-500" },
  { value: "high", label: "High", color: "text-orange-500" },
  { value: "urgent", label: "Urgent", color: "text-red-500" },
];

export const NewsPage = () => {
  const { getAuthHeaders, showStaffFeatures, isAdmin, user } = useAuth();
  const isCustomer = user?.user_type === "customer";
  const canCreateNews = showStaffFeatures() && !isCustomer;
  
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [creating, setCreating] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [viewingNews, setViewingNews] = useState(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const dragCounterRef = useRef(0);

  // Form state
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    html_content: "",
    images: [],
    category: "general",
    priority: "normal",
    source_email: "",
    source_sender: "",
    source_date: "",
    is_published: true,
    pdf_url: "",
    pdf_filename: "",
    pdf_file_id: "",
  });

  useEffect(() => {
    fetchNews();
    fetchCategories();
  }, [selectedCategory]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        published_only: "false",
        per_page: "50",
      });
      if (selectedCategory !== "all") {
        params.append("category", selectedCategory);
      }

      const response = await axios.get(`${API_URL}/news?${params}`, {
        headers: getAuthHeaders(),
      });
      setNews(response.data.news || []);
    } catch (error) {
      console.error("Failed to fetch news:", error);
      toast.error("Failed to load news");
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/news/categories/list`, {
        headers: getAuthHeaders(),
      });
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      setParsing(true);
      const response = await axios.post(`${API_URL}/news/parse-email`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });

      const parsed = response.data;
      
      // Check if email has (newsalert) tag
      if (!parsed.is_news_alert) {
        toast.warning(
          "This email doesn't have (newsalert) in the subject line. It may not be intended as a news bulletin.",
          { duration: 5000 }
        );
      }
      
      // Show image count if any
      if (parsed.images && parsed.images.length > 0) {
        toast.success(`Found ${parsed.images.length} image(s) in the email`);
      }
      
      // Use clean subject (without newsalert tag) as title
      setFormData({
        title: parsed.clean_subject || parsed.subject || "",
        content: parsed.body || "",
        html_content: parsed.html_body || "",
        images: parsed.images || [],
        category: parsed.suggested_category || "general",
        priority: parsed.suggested_priority || "normal",
        source_email: parsed.subject || "",
        source_sender: parsed.sender || "",
        source_date: parsed.date || "",
        is_published: true,
      });
      
      // Auto-show HTML preview if HTML content is available
      if (parsed.html_body) {
        setShowHtmlPreview(true);
      }

      toast.success("Email parsed successfully! Review and publish.");
      setShowCreateModal(true);
    } catch (error) {
      console.error("Failed to parse email:", error);
      toast.error(error.response?.data?.detail || "Failed to parse email file");
    } finally {
      setParsing(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Drag & Drop handlers - using counter pattern for reliable enter/leave tracking
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (dragCounterRef.current === 1) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Ensure dragging state is shown even if dragEnter was missed
    if (!isDragging && dragCounterRef.current > 0) {
      setIsDragging(true);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const fileName = file.name.toLowerCase();
    
    // Validate file type - allow .msg, .eml, and .pdf
    if (!fileName.endsWith('.msg') && !fileName.endsWith('.eml') && !fileName.endsWith('.pdf')) {
      toast.error("Please drop a .msg, .eml, or .pdf file");
      return;
    }

    // Handle PDF files differently
    if (fileName.endsWith('.pdf')) {
      // For PDF, create a news item with the PDF as content
      setFormData({
        title: file.name.replace('.pdf', '').replace(/_/g, ' '),
        content: `PDF Document: ${file.name}\n\nThis news bulletin was created from a PDF file. The file has been attached.`,
        category: "industry_news",
        priority: "normal",
        source_email: "",
        source_sender: "",
        source_date: new Date().toISOString(),
        is_published: true,
        pdf_file: file, // Store the file for upload
      });
      toast.success("PDF file ready! Complete the form and publish.");
      setShowCreateModal(true);
      return;
    }

    // Process email files (.msg, .eml)
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      setParsing(true);
      const response = await axios.post(`${API_URL}/news/parse-email`, formDataUpload, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });

      const parsed = response.data;
      
      if (!parsed.is_news_alert) {
        toast.warning(
          "This email doesn't have (newsalert) in the subject line. It may not be intended as a news bulletin.",
          { duration: 5000 }
        );
      }
      
      setFormData({
        title: parsed.clean_subject || parsed.subject || "",
        content: parsed.body || "",
        html_content: parsed.html_body || "",
        images: parsed.images || [],
        category: parsed.suggested_category || "general",
        priority: parsed.suggested_priority || "normal",
        source_email: parsed.subject || "",
        source_sender: parsed.sender || "",
        source_date: parsed.date || "",
        is_published: true,
        pdf_url: parsed.pdf_url || "",
        pdf_filename: parsed.pdf_filename || "",
        pdf_file_id: parsed.pdf_file_id || "",
      });
      
      // Auto-show HTML preview if HTML content is available
      if (parsed.html_body) {
        setShowHtmlPreview(true);
      }

      toast.success("Email parsed successfully! Review and publish.");
      setShowCreateModal(true);
    } catch (error) {
      console.error("Failed to parse email:", error);
      toast.error(error.response?.data?.detail || "Failed to parse email file");
    } finally {
      setParsing(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error("Title and content are required");
      return;
    }

    try {
      setCreating(true);
      
      if (editingNews) {
        await axios.put(`${API_URL}/news/${editingNews.id}`, formData, {
          headers: getAuthHeaders(),
        });
        toast.success("News updated successfully");
      } else {
        await axios.post(`${API_URL}/news`, formData, {
          headers: getAuthHeaders(),
        });
        toast.success("News bulletin created!");
      }

      setShowCreateModal(false);
      resetForm();
      fetchNews();
    } catch (error) {
      console.error("Failed to save news:", error);
      toast.error(error.response?.data?.detail || "Failed to save news");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (newsId) => {
    if (!window.confirm("Are you sure you want to delete this news bulletin?")) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/news/${newsId}`, {
        headers: getAuthHeaders(),
      });
      toast.success("News deleted");
      fetchNews();
    } catch (error) {
      console.error("Failed to delete news:", error);
      toast.error("Failed to delete news");
    }
  };

  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title: newsItem.title,
      content: newsItem.content,
      html_content: newsItem.html_content || "",
      images: newsItem.images || [],
      category: newsItem.category,
      priority: newsItem.priority,
      source_email: newsItem.source_email || "",
      source_sender: newsItem.source_sender || "",
      source_date: newsItem.source_date || "",
      is_published: newsItem.is_published,
      pdf_url: newsItem.pdf_url || "",
      pdf_filename: newsItem.pdf_filename || "",
      pdf_file_id: newsItem.pdf_file_id || "",
    });
    // Show HTML preview if HTML content exists
    setShowHtmlPreview(!!newsItem.html_content);
    setShowCreateModal(true);
  };

  const togglePublished = async (newsItem) => {
    try {
      await axios.put(`${API_URL}/news/${newsItem.id}`, {
        ...newsItem,
        is_published: !newsItem.is_published,
      }, {
        headers: getAuthHeaders(),
      });
      toast.success(newsItem.is_published ? "News unpublished" : "News published");
      fetchNews();
    } catch (error) {
      console.error("Failed to toggle publish:", error);
      toast.error("Failed to update news");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      content: "",
      html_content: "",
      images: [],
      category: "general",
      priority: "normal",
      source_email: "",
      source_sender: "",
      source_date: "",
      is_published: true,
      pdf_url: "",
      pdf_filename: "",
      pdf_file_id: "",
    });
    setEditingNews(null);
    setShowHtmlPreview(false);
  };

  const openCreateModal = () => {
    resetForm();
    setShowHtmlPreview(false);
    setShowCreateModal(true);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="news-page">
      <Navbar />
      <main className="pt-20 pb-8 px-4 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
                <Newspaper className="h-8 w-8 text-primary" />
                News & Bulletins
              </h1>
              <p className="text-muted-foreground mt-1">
                {canCreateNews && <span>Forward emails with <span className="font-mono bg-muted px-1.5 py-0.5 rounded text-xs">(newsalert)</span> in subject to import as news</span>}
              </p>
            </div>

            {canCreateNews && (
            <div className="flex items-center gap-3">
              {/* File upload for email parsing */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".msg,.eml,.pdf"
                onChange={handleFileUpload}
                className="hidden"
                id="email-upload"
              />
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={parsing}
                className="gap-2"
                data-testid="upload-email-btn"
              >
                {parsing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" />
                )}
                Import Email (.msg/.eml/.pdf)
              </Button>

              <Button onClick={openCreateModal} className="gap-2" data-testid="create-news-btn">
                <Plus className="h-4 w-4" />
                Create News
              </Button>
            </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-4 mt-6">
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48" data-testid="category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {SUBSCRIPTION_CATEGORIES.map((cat) => {
                  const config = CATEGORY_CONFIG[cat.value] || {};
                  const Icon = config.icon || Info;
                  return (
                    <SelectItem key={cat.value} value={cat.value}>
                      <span className="flex items-center gap-2">
                        <Icon className={`h-4 w-4 ${config.textColor || "text-gray-500"}`} />
                        {cat.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>

            <Button variant="ghost" size="icon" onClick={fetchNews} data-testid="refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </motion.div>

        {/* Drag & Drop Zone - Staff only */}
        {canCreateNews && (
        <motion.div
          ref={dropZoneRef}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`mb-6 border-2 border-dashed rounded-xl p-6 transition-all cursor-pointer ${
            isDragging 
              ? "border-primary bg-primary/5 scale-[1.02]" 
              : "border-muted-foreground/25 hover:border-primary/50 hover:bg-muted/30"
          }`}
          onClick={() => fileInputRef.current?.click()}
          data-testid="drop-zone"
        >
          <div className="flex flex-col items-center justify-center gap-3 text-center">
            {parsing ? (
              <>
                <Loader2 className="h-10 w-10 text-primary animate-spin" />
                <p className="text-sm font-medium text-foreground">Parsing email...</p>
              </>
            ) : isDragging ? (
              <>
                <Upload className="h-10 w-10 text-primary animate-bounce" />
                <p className="text-sm font-medium text-primary">Drop email file here</p>
              </>
            ) : (
              <>
                <div className="p-3 bg-muted rounded-full">
                  <Upload className="h-6 w-6 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Drag & drop file here
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    or click to browse • Supports .msg, .eml and .pdf files
                  </p>
                </div>
              </>
            )}
          </div>
        </motion.div>
        )}

        {/* News List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : news.length === 0 ? (
          <Card className="py-12 text-center">
            <CardContent>
              <Newspaper className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">No news bulletins yet</h3>
              <p className="text-muted-foreground mb-4">
                {canCreateNews ? "Drag & drop a file above, or create a news bulletin manually." : "Check back later for news updates."}
              </p>
              {canCreateNews && (
              <Button onClick={openCreateModal} className="gap-2">
                <Plus className="h-4 w-4" />
                Create News
              </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            <AnimatePresence mode="popLayout">
              {news.map((item, index) => {
                const categoryConfig = CATEGORY_CONFIG[item.category] || CATEGORY_CONFIG.general;
                const CategoryIcon = categoryConfig.icon;
                const priorityOption = PRIORITY_OPTIONS.find(p => p.value === item.priority);

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card 
                      className={`relative overflow-hidden cursor-pointer hover:shadow-md transition-shadow ${!item.is_published ? "opacity-60" : ""}`} 
                      data-testid={`news-item-${item.id}`}
                      onClick={() => setViewingNews(item)}
                    >
                      {/* Category indicator */}
                      <div className={`absolute left-0 top-0 bottom-0 w-1 ${categoryConfig.color}`} />
                      
                      <CardContent className="p-6 pl-8">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            {/* Meta info */}
                            <div className="flex items-center gap-3 mb-2">
                              <span className={`flex items-center gap-1.5 text-xs font-medium ${categoryConfig.textColor}`}>
                                <CategoryIcon className="h-3.5 w-3.5" />
                                {categoryConfig.label}
                              </span>
                              <span className={`text-xs font-medium ${priorityOption?.color}`}>
                                {priorityOption?.label}
                              </span>
                              {!item.is_published && (
                                <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded">
                                  Draft
                                </span>
                              )}
                              {item.pdf_url && (
                                <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded flex items-center gap-1">
                                  <FileText className="h-3 w-3" />
                                  PDF
                                </span>
                              )}
                            </div>

                            {/* Title */}
                            <h3 className="text-lg font-semibold text-foreground mb-2">
                              {item.title}
                            </h3>

                            {/* Images preview - only show valid images */}
                            {item.images && item.images.length > 0 && item.images.some(img => img.data && (img.data.startsWith('data:image') || img.data.startsWith('http'))) && (
                              <div className="flex gap-2 mb-2 overflow-x-auto">
                                {item.images
                                  .filter(img => img.data && (img.data.startsWith('data:image') || img.data.startsWith('http')))
                                  .slice(0, 3)
                                  .map((img, idx) => (
                                  <img 
                                    key={idx}
                                    src={img.data} 
                                    alt={img.filename || `Image ${idx + 1}`}
                                    className="h-16 w-24 object-cover rounded border flex-shrink-0"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                ))}
                                {item.images.filter(img => img.data && (img.data.startsWith('data:image') || img.data.startsWith('http'))).length > 3 && (
                                  <div className="h-16 w-24 bg-muted rounded border flex items-center justify-center flex-shrink-0">
                                    <span className="text-xs text-muted-foreground">+{item.images.filter(img => img.data && (img.data.startsWith('data:image') || img.data.startsWith('http'))).length - 3} more</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Content preview */}
                            {item.html_content ? (
                              <div 
                                className="text-sm text-muted-foreground line-clamp-2 mb-3 prose prose-sm max-w-none"
                                dangerouslySetInnerHTML={{ __html: item.html_content.replace(/<[^>]*>/g, ' ').substring(0, 200) + '...' }}
                              />
                            ) : (
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                                {item.content}
                              </p>
                            )}

                            {/* Footer info */}
                            <div className="flex items-center gap-4 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                {new Date(item.created_at).toLocaleDateString()}
                              </span>
                              {item.source_sender && (
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {item.source_sender}
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); togglePublished(item); }}
                              title={item.is_published ? "Unpublish" : "Publish"}
                              data-testid={`toggle-publish-${item.id}`}
                            >
                              {item.is_published ? (
                                <Eye className="h-4 w-4 text-green-500" />
                              ) : (
                                <EyeOff className="h-4 w-4 text-gray-400" />
                              )}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); handleEdit(item); }}
                              data-testid={`edit-news-${item.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={(e) => { e.stopPropagation(); handleDelete(item.id); }}
                              className="text-destructive hover:text-destructive"
                              data-testid={`delete-news-${item.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showCreateModal} onOpenChange={(open) => {
          if (!open) resetForm();
          setShowCreateModal(open);
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Newspaper className="h-5 w-5" />
                {editingNews ? "Edit News Bulletin" : "Create News Bulletin"}
              </DialogTitle>
              <DialogDescription>
                {editingNews
                  ? "Update the news bulletin details"
                  : "Create a new news bulletin from email content or write manually"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter news title..."
                  data-testid="news-title-input"
                />
              </div>

              {/* Category & Priority */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                  >
                    <SelectTrigger data-testid="news-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBSCRIPTION_CATEGORIES.map((cat) => {
                        const config = CATEGORY_CONFIG[cat.value] || {};
                        const Icon = config.icon || Info;
                        return (
                          <SelectItem key={cat.value} value={cat.value}>
                            <span className="flex items-center gap-2">
                              <Icon className={`h-4 w-4 ${config.textColor || "text-gray-500"}`} />
                              {cat.label}
                            </span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select
                    value={formData.priority}
                    onValueChange={(value) => setFormData({ ...formData, priority: value })}
                  >
                    <SelectTrigger data-testid="news-priority-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PRIORITY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          <span className={option.color}>{option.label}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Content */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="content">Content *</Label>
                  {formData.html_content && (
                    <button
                      type="button"
                      onClick={() => setShowHtmlPreview(!showHtmlPreview)}
                      className="text-xs flex items-center gap-1 px-2 py-1 rounded bg-muted hover:bg-muted/80 transition-colors"
                    >
                      {showHtmlPreview ? (
                        <>
                          <FileText className="h-3 w-3" />
                          Edit Plain Text
                        </>
                      ) : (
                        <>
                          <Eye className="h-3 w-3" />
                          Preview HTML
                        </>
                      )}
                    </button>
                  )}
                </div>
                
                {showHtmlPreview && formData.html_content ? (
                  <div 
                    className="border rounded-lg p-4 bg-white min-h-[200px] max-h-[400px] overflow-y-auto prose prose-sm max-w-none news-html-content"
                    dangerouslySetInnerHTML={{ __html: formData.html_content }}
                  />
                ) : (
                  <Textarea
                    id="content"
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Enter news content..."
                    rows={8}
                    data-testid="news-content-input"
                  />
                )}
                
                {formData.html_content && !showHtmlPreview && (
                  <p className="text-xs text-blue-600 flex items-center gap-1">
                    <Mail className="h-3 w-3" />
                    HTML content detected from email - click "Preview HTML" to see formatted version
                  </p>
                )}
              </div>

              {/* Images from email - only show valid images */}
              {formData.images && formData.images.length > 0 && formData.images.some(img => img.data && (img.data.startsWith('data:image') || img.data.startsWith('http'))) && (
                <div className="space-y-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <Label className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-blue-500" />
                    Attached Images ({formData.images.filter(img => img.data && (img.data.startsWith('data:image') || img.data.startsWith('http'))).length})
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    {formData.images
                      .filter(img => img.data && (img.data.startsWith('data:image') || img.data.startsWith('http')))
                      .map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img 
                          src={img.data} 
                          alt={img.filename || `Image ${idx + 1}`}
                          className="rounded-lg border shadow-sm max-h-48 w-full object-contain bg-white"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 rounded-b-lg truncate">
                          {img.filename}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            const newImages = formData.images.filter((_, i) => i !== idx);
                            setFormData({ ...formData, images: newImages });
                          }}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-blue-600">These images will be saved with the news bulletin</p>
                </div>
              )}

              {/* Source Info (collapsible) */}
              <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
                <h4 className="text-sm font-medium">Source Information (optional)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="source_sender">Original Sender</Label>
                    <Input
                      id="source_sender"
                      value={formData.source_sender}
                      onChange={(e) => setFormData({ ...formData, source_sender: e.target.value })}
                      placeholder="e.g., Luka Koper Info"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="source_date">Original Date</Label>
                    <Input
                      id="source_date"
                      value={formData.source_date}
                      onChange={(e) => setFormData({ ...formData, source_date: e.target.value })}
                      placeholder="e.g., 2026-02-16"
                    />
                  </div>
                </div>
              </div>

              {/* Publish toggle */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <Label className="text-base">Publish immediately</Label>
                  <p className="text-sm text-muted-foreground">
                    Users will see this news as a popup when they enter the app
                  </p>
                </div>
                <Switch
                  checked={formData.is_published}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
                  data-testid="news-publish-toggle"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={creating} data-testid="save-news-btn">
                {creating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : editingNews ? (
                  "Update News"
                ) : (
                  "Create News"
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View News Modal */}
        <Dialog open={!!viewingNews} onOpenChange={() => setViewingNews(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            {viewingNews && (
              <>
                <DialogHeader>
                  <div className="flex items-center gap-2 mb-2">
                    {(() => {
                      const catConfig = CATEGORY_CONFIG[viewingNews.category] || CATEGORY_CONFIG.general;
                      const CatIcon = catConfig.icon;
                      return (
                        <span className={`flex items-center gap-1.5 text-xs font-medium ${catConfig.textColor}`}>
                          <CatIcon className="h-3.5 w-3.5" />
                          {catConfig.label}
                        </span>
                      );
                    })()}
                    <span className="text-xs text-muted-foreground">
                      {new Date(viewingNews.created_at).toLocaleDateString()}
                    </span>
                    {viewingNews.source_sender && (
                      <span className="text-xs text-muted-foreground">• {viewingNews.source_sender}</span>
                    )}
                  </div>
                  <DialogTitle className="text-xl">{viewingNews.title}</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* PDF attachment button */}
                  {viewingNews.pdf_url && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="h-8 w-8 text-red-600" />
                        <div>
                          <p className="font-medium text-red-800">PDF Attachment</p>
                          <p className="text-sm text-red-600">{viewingNews.pdf_filename || "Document.pdf"}</p>
                        </div>
                      </div>
                      <Button 
                        onClick={() => window.open(viewingNews.pdf_url, '_blank')}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open PDF
                      </Button>
                    </div>
                  )}

                  {/* Images */}
                  {viewingNews.images && viewingNews.images.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">Attachments</p>
                      <div className="flex flex-wrap gap-3">
                        {viewingNews.images
                          .filter(img => img.data && (img.data.startsWith('data:image') || img.data.startsWith('http')))
                          .map((img, idx) => (
                          <a 
                            key={idx} 
                            href={img.data} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="block"
                          >
                            <img 
                              src={img.data} 
                              alt={img.filename || `Image ${idx + 1}`}
                              className="h-32 w-auto object-contain rounded border hover:shadow-lg transition-shadow cursor-pointer"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Content */}
                  <div className="prose prose-sm max-w-none">
                    {viewingNews.html_content ? (
                      <div 
                        className="news-html-content"
                        dangerouslySetInnerHTML={{ __html: viewingNews.html_content }}
                      />
                    ) : (
                      <div className="whitespace-pre-wrap text-foreground">
                        {viewingNews.content}
                      </div>
                    )}
                  </div>
                </div>

                <DialogFooter className="flex gap-2">
                  <Button variant="outline" onClick={() => setViewingNews(null)}>Close</Button>
                  {canCreateNews && (
                    <Button onClick={() => { handleEdit(viewingNews); setViewingNews(null); }}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default NewsPage;
